import React, { memo, useCallback, useEffect, useMemo, useState } from 'react'
import { useRouter } from 'next/router'
import { endOfMonth, format } from 'date-fns'
import _ from 'lodash'

import { Button, Modal } from 'components/Common'
import { ProviderInfo } from 'components/Providers/components/ProviderCard/components/ProviderInfo'
import BookingSelection from './BookingSelection'
import BookingApproval from './BookingApproval'
import { NavNext, NavPrev } from 'components/Icons'
import BookingConfirmation from './BookingConfirmation'
import Divider from './Divider'
import {
  getRelatedPersonId,
  mergeStyles,
  appointmentCreate,
  appointmentGetSlots,
  appointmentReschedule,
  checkTypeAndConvertToDate,
  convertToProviderTimezone,
  getTimeSlotsForMonth,
  getProvider
} from '@enlyt/base'
import useModalWidthResizer from 'hooks/useModalWidthResizer'

import defaultStyles from './styles'
import { useSelector } from 'react-redux'

// _range doesn't include last value
export const ADDITIONAL_DAY_FOR_RANGE = 1
export const AVAILABLE_DAYS_FOR_REQUEST = 15
const DATE_FORMAT = 'yyyy-MM-dd'

const AppointmentBookingModal = props => {
  const styles = mergeStyles(defaultStyles, props.style)
  const { appointmentId, appointmentIdType, setIsOpenModal, isOpenModal } =
    props
  const { avatar, name, specialization, rating, isInNetwork } = props.provider
  const modalWidth = useModalWidthResizer()
  const router = useRouter()
  const currentDate = useMemo(
    () => new Date(convertToProviderTimezone()),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  )
  const currentDateNumber = useMemo(
    () => currentDate.getDate(),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  )
  const currentLastMonthDate = useMemo(
    () => endOfMonth(currentDate),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  )
  const currentLastMonthDateNumber = useMemo(
    () => endOfMonth(currentDate).getDate(),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  )

  const initialAvailableDaysInCurrentMonth = useMemo(() => {
    return _.range(
      currentDateNumber,
      currentLastMonthDateNumber + ADDITIONAL_DAY_FOR_RANGE,
      1
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const [modalStep, setModalStep] = useState(1)
  const [appointmentData, setAppointmentData] = useState(null)
  const [availableDaysInCurrentMonth, setAvailableDaysInCurrentMonth] =
    useState(initialAvailableDaysInCurrentMonth)
  const [locationSelected, setLocationSelected] = useState(null)
  const [reasonForVisitSelected, setReasonForVisitSelected] = useState(null)
  const [availableScheduleDays, setAvailableScheduleDays] = useState(null)
  const [selectedDate, setSelectedDate] = useState(null)
  const [selectedDayIndex, setSelectedDayIndex] = useState(null)
  const [selectedMonthIndex, setSelectedMonthIndex] = useState(0)
  const [activeDateSlideIndex, setActiveDateSlideIndex] = useState(0)
  const [timeslots, setTimeslots] = useState([])
  const [availableTimeslots, setAvailableTimeslots] = useState([])
  const [selectedTimeslot, setSelectedTimeSlot] = useState(null)
  const [isNoTimeSlotError, setIsNoTimeSlotError] = useState(false)

  const patientId = useSelector(getRelatedPersonId)

  const [
    getInitialFirstPartTimeSlots,
    {
      loading: loadingInitialFirstPartTimeSlots,
      error: errorInitialFirstPartTimeSlots,
      data: dataInitialFirstPartTimeSlots
    }
  ] = appointmentGetSlots()

  const [
    getInitialSecondPartTimeSlots,
    {
      loading: loadingInitialSecondPartTimeSlots,
      error: errorInitialSecondPartTimeSlots,
      data: dataInitialSecondPartTimeSlots
    }
  ] = appointmentGetSlots()

  const [
    getFirstPartNewTimeSlotsForMonth,
    {
      loading: loadingFirstPartNewTimeSlots,
      error: errorFirstPartNewTimeSlots,
      data: dataFirstPartNewTimeSlots
    }
  ] = appointmentGetSlots()

  const [
    getSecondPartNewTimeSlotsForMonth,
    {
      loading: loadingSecondPartNewTimeSlots,
      error: errorSecondPartNewTimeSlots,
      data: dataSecondPartNewTimeSlots
    }
  ] = appointmentGetSlots()

  const [
    createAppointment,
    {
      loading: isLoadingOfCreatingAppointment,
      error: isErrorOfCreatingAppointment,
      data: createdAppointmentsData
    }
  ] = appointmentCreate()

  const [
    rescheduleAppointment,
    {
      loading: isLoadingOfReschedulingAppointment,
      error: isErrorOfReschedulingAppointment,
      data: rescheduledAppointmentsData
    }
  ] = appointmentReschedule()

  const onLocationSelected = locationId => {
    setIsNoTimeSlotError(false)
    setLocationSelected(
      appointmentData.departments.find(
        department => department.departmentId === locationId
      )
    )
    setAvailableScheduleDays(
      appointmentData.scheduleDays.filter(
        day =>
          day.departmentId === locationId &&
          day.visitTypeId === reasonForVisitSelected.visitTypeId
      )
    )
  }

  const onReasonForVisitSelected = reasonForVisitId => {
    setIsNoTimeSlotError(false)
    setReasonForVisitSelected(
      appointmentData.visitTypes.find(
        visit => visit.visitTypeId === reasonForVisitId
      )
    )
    setAvailableScheduleDays(
      appointmentData.scheduleDays.filter(
        d =>
          d.departmentId === locationSelected?.departmentId &&
          d.visitTypeId === reasonForVisitId
      )
    )
  }

  const closeModal = () => {
    setIsOpenModal(false)
    setModalStep(1)

    setLocationSelected(null)
    setReasonForVisitSelected(null)

    setAvailableScheduleDays(null)
    setSelectedTimeSlot(null)
    setSelectedDate(null)
    setSelectedDayIndex(null)
    setSelectedMonthIndex(0)

    setIsNoTimeSlotError(false)

    if (createdAppointmentsData || rescheduledAppointmentsData) {
      router.push('/appointments')
    }
  }

  const getInitialTimeSlots = async () => {
    const differenceBetweenDays = currentLastMonthDateNumber - currentDateNumber
    const isMoreThanAvailableNumberOfDaysForRequest =
      differenceBetweenDays > AVAILABLE_DAYS_FOR_REQUEST
    const currentYear = currentDate.getFullYear()
    const currentMonth = currentDate.getMonth()
    const formattedCurrentDate = format(currentDate, DATE_FORMAT)
    const formattedLastMonthDate = format(currentLastMonthDate, DATE_FORMAT)
    const provider = await getProvider()

    if (isMoreThanAvailableNumberOfDaysForRequest) {
      const firstDates = {
        startDate: formattedCurrentDate,
        endDate: format(
          new Date(currentYear, currentMonth, AVAILABLE_DAYS_FOR_REQUEST),
          DATE_FORMAT
        )
      }

      const lastDates = {
        startDate: format(
          new Date(currentYear, currentMonth, AVAILABLE_DAYS_FOR_REQUEST + 1),
          DATE_FORMAT
        ),
        endDate: formattedLastMonthDate
      }

      Promise.all([
        getInitialFirstPartTimeSlots({
          variables: { ...provider, ...firstDates }
        }),
        getInitialSecondPartTimeSlots({
          variables: { ...provider, ...lastDates }
        })
      ]).catch(err =>
        console.log('getting Initial TimeSlots for two requests error', err)
      )
    } else {
      const dates = {
        startDate: formattedCurrentDate,
        endDate: formattedLastMonthDate
      }

      try {
        await getInitialFirstPartTimeSlots({
          variables: { ...provider, ...dates }
        })
      } catch (err) {
        console.log('getting Initial TimeSlots for one request error', err)
      }
    }
  }

  useEffect(() => {
    getInitialTimeSlots()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    // set initial timeslots
    if (dataInitialFirstPartTimeSlots && dataInitialSecondPartTimeSlots) {
      const firstNewSlots = dataInitialFirstPartTimeSlots.appointmentSlots
      const secondNewSlots = dataInitialSecondPartTimeSlots.appointmentSlots
      const newTimeSlots = getTimeSlotsForMonth(firstNewSlots, secondNewSlots)

      setAppointmentData(newTimeSlots)
      return
    }

    if (dataInitialFirstPartTimeSlots) {
      setAppointmentData(dataInitialFirstPartTimeSlots.appointmentSlots)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataInitialFirstPartTimeSlots, dataInitialSecondPartTimeSlots])

  useEffect(() => {
    // reassign available slots by opening modal if timeslots are here
    if (appointmentData) {
      setLocationSelected(appointmentData.departments[0])
      setReasonForVisitSelected(appointmentData.visitTypes[0])

      setAvailableScheduleDays(
        appointmentData.scheduleDays.filter(day => {
          return (
            day.departmentId === appointmentData.departments[0].departmentId &&
            day.visitTypeId === appointmentData.visitTypes[0].visitTypeId
          )
        })
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    // request for month
    if (dataFirstPartNewTimeSlots && dataSecondPartNewTimeSlots) {
      const firstNewSlots = dataFirstPartNewTimeSlots.appointmentSlots
      const secondNewSlots = dataSecondPartNewTimeSlots.appointmentSlots
      const newTimeSlots = getTimeSlotsForMonth(firstNewSlots, secondNewSlots)

      setAppointmentData(newTimeSlots)
    }
  }, [dataFirstPartNewTimeSlots, dataSecondPartNewTimeSlots])

  useEffect(() => {
    // update timeslots
    if (appointmentData) {
      if (!locationSelected) {
        setLocationSelected(appointmentData.departments[0])
      }

      if (!reasonForVisitSelected) {
        setReasonForVisitSelected(appointmentData.visitTypes[0])
      }

      if (dataFirstPartNewTimeSlots && dataSecondPartNewTimeSlots) {
        if (locationSelected && reasonForVisitSelected) {
          setAvailableScheduleDays(
            appointmentData.scheduleDays.filter(day => {
              return (
                day.departmentId === locationSelected.departmentId &&
                day.visitTypeId === reasonForVisitSelected.visitTypeId
              )
            })
          )
        } else {
          setLocationSelected(appointmentData.departments[0])
          setReasonForVisitSelected(appointmentData.visitTypes[0])
        }
      } else {
        setAvailableScheduleDays(
          appointmentData.scheduleDays.filter(day => {
            return (
              day.departmentId ===
                appointmentData.departments[0].departmentId &&
              day.visitTypeId === appointmentData.visitTypes[0].visitTypeId
            )
          })
        )
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appointmentData])

  useEffect(() => {
    // cancel error if timeslot or selected date were changed
    if (isNoTimeSlotError) {
      setIsNoTimeSlotError(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTimeslot, selectedDate])

  useEffect(() => {
    // set available timeslots
    if (availableScheduleDays) {
      const result = availableScheduleDays.reduce((days, day) => {
        const slots = day.slots.reduce((slots, daySlot) => {
          const slot = {
            id: `${day.date}${daySlot.time}`,
            date: new Date(`${day.date},${daySlot.time}`)
          }

          return [...slots, slot]
        }, [])

        return [...days, ...slots]
      }, [])

      setAvailableTimeslots(result)
    }
  }, [availableScheduleDays])

  useEffect(() => {
    // set timeslots
    const slots = availableTimeslots.map(slot => ({
      id: slot.id,
      date: checkTypeAndConvertToDate(slot.date)
    }))

    setTimeslots(slots)
  }, [availableTimeslots])

  const isRescheduling = useMemo(() => {
    return router.pathname.includes('visits')
  }, [router])

  const bookAppointment = async () => {
    setModalStep(modalStep + 1)
    const provider = await getProvider()

    const variables = {
      patientId,
      departmentId: locationSelected.departmentId,
      departmentIdType: locationSelected.departmentIdType,
      visitTypeId: reasonForVisitSelected.visitTypeId,
      visitTypeIdType: reasonForVisitSelected.visitTypeIdType,
      date: format(selectedTimeslot.date, DATE_FORMAT),
      providerId: provider.providerId,
      providerIdType: provider.providerIdType,
      time: format(selectedTimeslot.date, 'HH:mm')
    }

    if (!appointmentData) {
      return
    }

    if (isRescheduling) {
      try {
        await rescheduleAppointment({
          variables: { ...variables, appointmentId, appointmentIdType }
        })
      } catch (err) {
        console.log('Appointment rescheduling error', err)
      }
    } else {
      try {
        await createAppointment({ variables })
      } catch (err) {
        console.log('Appointment creation error', err)
      }
    }
  }

  const selectTimeSlot = id => {
    if (id === null) {
      setSelectedTimeSlot(null)
    }

    if (selectedTimeslot?.id === id) {
      return
    }

    const timeSlot = availableTimeslots.find(d => d.id === id)
    setSelectedTimeSlot(timeSlot)
  }

  const onNextStepHandler = useCallback(() => {
    if (selectedTimeslot) {
      setModalStep(prev => prev + 1)
    } else {
      setIsNoTimeSlotError(true)
    }
  }, [selectedTimeslot])

  const getButtons = modalStep => {
    switch (modalStep) {
      case 1:
        return [
          <>
            {isNoTimeSlotError && (
              <p style={styles.noTimeSlotError}>
                Please choose an appointment time slot
              </p>
            )}
            <div
              key='next'
              style={{
                ...styles.actionText,
                ...(!selectedTimeslot && styles.disabledAction)
              }}
              onClick={onNextStepHandler}
            >
              Next <NavNext />
            </div>
          </>
        ]
      case 2:
        return [
          <div
            key='back'
            style={styles.actionText}
            onClick={() => setModalStep(modalStep - 1)}
          >
            <NavPrev /> Back
          </div>
        ]
      default:
        return null
    }
  }

  const getModalStyle = modalStep => {
    switch (modalStep) {
      case 1:
        return styles.modalNext
      case 2:
        return styles.modalBack
      default:
        return styles.modal
    }
  }

  const startMeeting = useCallback(() => {
    router.push('/videoSession')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <Modal
      onClose={closeModal}
      open={isOpenModal}
      header={isRescheduling ? 'Reschedule Appointment' : 'Book an Appointment'}
      style={getModalStyle(modalStep)}
      buttons={getButtons(modalStep)}
      withoutScrolling
      width={`${modalWidth}px`}
    >
      <ProviderInfo
        avatar={avatar}
        name={name}
        specialization={specialization}
        rating={rating}
        isInNetwork={isInNetwork}
        style={styles.providerInfo}
      />

      <Divider />

      {/* TODO condition */}
      {!loadingInitialFirstPartTimeSlots &&
        !loadingInitialSecondPartTimeSlots &&
        modalStep === 1 &&
        router.pathname.includes('providers') &&
        name === 'Seth Draper' && (
          <div style={styles.visitContainer}>
            <Button onClick={startMeeting} style={styles.buttonVisit}>
              Start a Virtual Visit
            </Button>
            <div style={styles.textDividerContainer}>
              <div style={styles.line} />
              <p style={styles.textDivider}>OR BOOK AN OFFICE VISIT</p>
              <div style={styles.line} />
            </div>
          </div>
        )}

      {modalStep === 1 && (
        <BookingSelection
          loadingInitialTimeSlots={
            loadingInitialFirstPartTimeSlots ||
            loadingInitialSecondPartTimeSlots
          }
          errorInitialTimeSlots={
            errorInitialFirstPartTimeSlots || errorInitialSecondPartTimeSlots
          }
          loadingOtherTimeSlots={
            loadingFirstPartNewTimeSlots || loadingSecondPartNewTimeSlots
          }
          errorOtherTimeSlots={
            errorFirstPartNewTimeSlots || errorSecondPartNewTimeSlots
          }
          departments={appointmentData && appointmentData.departments}
          locationSelected={locationSelected}
          onLocationSelected={onLocationSelected}
          visitTypes={appointmentData && appointmentData.visitTypes}
          reasonForVisitSelected={reasonForVisitSelected}
          onReasonForVisitSelected={onReasonForVisitSelected}
          selectedTimeslot={selectedTimeslot}
          selectedDate={selectedDate}
          setSelectedDate={setSelectedDate}
          selectedDayIndex={selectedDayIndex}
          setSelectedDayIndex={setSelectedDayIndex}
          selectedMonthIndex={selectedMonthIndex}
          setSelectedMonthIndex={setSelectedMonthIndex}
          activeDateSlideIndex={activeDateSlideIndex}
          setActiveDateSlideIndex={setActiveDateSlideIndex}
          availableDaysInCurrentMonth={availableDaysInCurrentMonth}
          setAvailableDaysInCurrentMonth={setAvailableDaysInCurrentMonth}
          timeslots={timeslots}
          selectTimeSlot={selectTimeSlot}
          providerId={props.provider.id}
          providerIdType={props.provider.providerIdType}
          getFirstPartNewTimeSlotsForMonth={getFirstPartNewTimeSlotsForMonth}
          getSecondPartNewTimeSlotsForMonth={getSecondPartNewTimeSlotsForMonth}
        />
      )}

      {modalStep === 2 && (
        <BookingConfirmation
          selectedTimeslot={selectedTimeslot}
          locationSelected={locationSelected}
          onBookAppointment={bookAppointment}
        />
      )}

      {modalStep === 3 && (
        <BookingApproval
          selectedTimeslot={selectedTimeslot}
          locationSelected={locationSelected}
          isLoadingOfCreatingAppointment={isLoadingOfCreatingAppointment}
          isErrorOfCreatingAppointment={isErrorOfCreatingAppointment}
          isLoadingOfReschedulingAppointment={
            isLoadingOfReschedulingAppointment
          }
          isErrorOfReschedulingAppointment={isErrorOfReschedulingAppointment}
        />
      )}
    </Modal>
  )
}

export default memo(AppointmentBookingModal)
